import { Menu, Search, ShoppingBag, X } from "lucide-react";
import type { FormEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { brand, navItems } from "../config/brand";
import { useCatalog } from "../hooks/useCatalog";
import { useCart } from "../lib/cart";
import { navigateTo } from "../lib/navigation";
import { Button } from "./Button";
import { SmartLink } from "./SmartLink";

export function Header() {
  const cart = useCart();
  const { menu } = useCatalog();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const searchResults = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return menu.slice(0, 4);
    return menu
      .filter((item) =>
        [item.name, item.subtitle, item.description, item.category, ...item.tags]
          .join(" ")
          .toLowerCase()
          .includes(term)
      )
      .slice(0, 5);
  }, [menu, query]);

  useEffect(() => {
    if (searchOpen) window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [searchOpen]);

  useEffect(() => {
    const appRoot = document.getElementById("root");
    document.body.classList.toggle("nav-drawer-open", open);
    appRoot?.toggleAttribute("inert", open);
    if (!open) {
      appRoot?.removeAttribute("inert");
      return () => document.body.classList.remove("nav-drawer-open");
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("nav-drawer-open");
      appRoot?.removeAttribute("inert");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const term = query.trim();
    setSearchOpen(false);
    navigateTo(term ? `/menus?search=${encodeURIComponent(term)}` : "/menus");
  }

  function renderLinks() {
    return navItems.map((item) => (
      <SmartLink key={item.href} href={item.href} onClick={() => setOpen(false)}>
        {item.label}
      </SmartLink>
    ));
  }

  const mobileDrawer = (
    <>
      <div
        className={`mobile-menu-backdrop ${open ? "mobile-menu-backdrop-open" : ""}`}
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />
      <div
        id="mobile-menu"
        className={`mobile-menu ${open ? "mobile-menu-open" : ""}`}
        role="dialog"
        aria-modal={open ? true : undefined}
        aria-hidden={!open}
        inert={!open ? true : undefined}
        data-testid="mobile-menu"
      >
        <div className="mobile-menu-header">
          <img src={brand.logo} alt={brand.markAlt} />
          <button className="icon-button" type="button" aria-label="Close menu" onClick={() => setOpen(false)}>
            <X size={22} />
          </button>
        </div>
        <nav aria-label="Mobile navigation">{renderLinks()}</nav>
        <Button
          onClick={() => {
            setOpen(false);
            navigateTo("/private-chef");
          }}
        >
          Book a table / hire chef
        </Button>
      </div>
    </>
  );

  return (
    <>
      <header className="site-header">
        <button
          className="icon-button mobile-only"
          type="button"
          aria-label="Open menu"
          aria-controls="mobile-menu"
          aria-expanded={open}
          onClick={() => {
            setSearchOpen(false);
            setOpen(true);
          }}
        >
          <Menu size={22} />
        </button>
        <SmartLink href="/" className="brand-lockup">
          <img src={brand.logo} alt={brand.markAlt} />
        </SmartLink>
        <nav className="desktop-nav" aria-label="Main navigation">
          {renderLinks()}
        </nav>
        <div className="header-actions">
          <button
            className="icon-button"
            type="button"
            aria-label={searchOpen ? "Close search" : "Search"}
            aria-expanded={searchOpen}
            onClick={() => {
              setOpen(false);
              setSearchOpen((current) => !current);
            }}
          >
            <Search size={19} />
          </button>
          <button
            className="icon-button cart-button"
            type="button"
            aria-label="Open cart"
            data-testid="open-cart"
            onClick={() => cart.setOpen(true)}
          >
            <ShoppingBag size={19} />
            {cart.count ? <span>{cart.count}</span> : null}
          </button>
          <Button variant="dark" className="desktop-cta" onClick={() => navigateTo("/private-chef")}>
            Book a table / hire chef
          </Button>
        </div>
        {searchOpen ? (
          <div className="search-panel" role="search">
            <form onSubmit={submitSearch}>
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search menus"
                aria-label="Search menus"
              />
              <Button type="submit" variant="dark">
                Search
              </Button>
              <button className="icon-button" type="button" aria-label="Close search" onClick={() => setSearchOpen(false)}>
                <X size={18} />
              </button>
            </form>
            <div className="search-results">
              {searchResults.length ? (
                searchResults.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setQuery(item.name);
                      setSearchOpen(false);
                      navigateTo(`/menus?search=${encodeURIComponent(item.name)}`);
                    }}
                  >
                    <span>{item.name}</span>
                    <small>{item.subtitle}</small>
                  </button>
                ))
              ) : (
                <p>No matching menu items.</p>
              )}
            </div>
          </div>
        ) : null}
      </header>
      {createPortal(mobileDrawer, document.body)}
    </>
  );
}
